/**
 * GET /fm/shell — what the FM menu and top bar need beyond permissions
 * (FM Manager rebuild C0.2): which optional modules are on, the client's
 * name, and the caller's sites with their zones. Fetched only for the FM
 * experience, so no other role's shell sends a request it did not send before.
 *
 * The counts beside menu items (C0.1) are their own request,
 * GET /fm/shell?parts=counts (`useFmCounts`, owner decision F1): they are the
 * home's figures, and the site list the home waits for must not wait for them.
 */
import { useQuery } from '@tanstack/react-query';
import { create } from 'zustand';
import { apiGet } from '@/utils/api';
import { useAuthStore } from '@/stores/authStore';
import { isFmExperience, type FmModule } from '@/config/fmExperience';

export interface FmSite { id: string; name: string; timezone: string | null }
export type FmCounts = Partial<Record<'tickets' | 'tickets_breached' | 'compliance' | 'ppm_overdue' | 'vendors', number>>;
export interface FmShell {
  modules: Record<FmModule, boolean>;
  client_name: string | null;
  all_sites: boolean;
  sites: FmSite[];
  org_timezone: string;
  default_building_id: string | null;
}

export function useFmShell() {
  const user = useAuthStore((s) => s.user);
  return useQuery({
    queryKey: ['fm', 'shell'],
    queryFn: () => apiGet<FmShell>('/fm/shell'),
    enabled: isFmExperience(user),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}

/** The menu's count bubbles (C0.1). Keyed under ['fm', 'shell'] so every
 *  invalidation of the shell refreshes them too. */
export function useFmCounts() {
  const user = useAuthStore((s) => s.user);
  return useQuery({
    queryKey: ['fm', 'shell', 'counts'],
    queryFn: () => apiGet<{ counts: FmCounts }>('/fm/shell?parts=counts').then((d) => d.counts),
    enabled: isFmExperience(user),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}

const SITE_KEY = 'spaciora-fm-site';

function readSite(): string | null {
  try {
    return localStorage.getItem(SITE_KEY);
  } catch {
    return null;
  }
}

/** The site the FM picked in the top bar (C0.2); null = all my sites.
 *  Remembered on this device only — a convenience, never a scope. */
export const useFmSite = create<{ site: string | null; setSite: (s: string | null) => void }>(
  (set) => ({
    site: readSite(),
    setSite: (site) => {
      try {
        if (site) localStorage.setItem(SITE_KEY, site);
        else localStorage.removeItem(SITE_KEY);
      } catch {
        /* forgetting is acceptable */
      }
      set({ site });
    },
  }));

/** The selected site, if it is still one of mine (a site removed from my
 *  list since it was picked falls back to all). */
export function selectedSite(shell: FmShell | undefined, site: string | null): FmSite | null {
  if (!shell || !site) return null;
  return shell.sites.find((s) => s.id === site) ?? null;
}

/** The zone the FM clock and greeting use (I8, owner decision W2): the
 *  selected site's, else the default building's, else the first of the
 *  caller's sites that has a zone — so "All sites" over sites that share one
 *  zone reads that zone, not the organisation's UTC. The organisation's zone
 *  only when none of the caller's sites has one (or there are none). */
export function fmZone(shell: FmShell | undefined, site: string | null): string | undefined {
  if (!shell) return undefined;
  const picked = selectedSite(shell, site);
  if (picked?.timezone) return picked.timezone;
  const def = shell.sites.find((s) => s.id === shell.default_building_id);
  if (def?.timezone) return def.timezone;
  const first = shell.sites.find((s) => s.timezone);
  if (first?.timezone) return first.timezone;
  return shell.org_timezone || undefined;
}
